import {
	Configure,
	InstantSearch,
	InstantSearchProps,
	useConfigure,
	useInfiniteHits,
	useInstantSearch,
	usePagination
} from 'react-instantsearch-hooks-web'
import {Jobsfilters, algoliaFilterMap} from './jobsfilters'
import React, {HTMLProps, Suspense, useEffect, useMemo, useState} from 'react'
import {getUnixTime, startOfDay, sub} from 'date-fns'

import {Autocomplete} from 'blocks/search/Autocomplete'
import {Block} from 'blocks/block'
import {DPJobsBlock} from 'types/DPTypes'
import type {Hit} from 'instantsearch.js'
import {Icon} from 'assets/icon'
import {JobAlerts} from './jobalerts'
import {Link} from 'util/link'
import {SearchresultsMore} from 'layout/search/searchresults'
import {Theme} from 'layout/theme'
import algoliasearch from 'algoliasearch'
import css from './jobs.module.scss'
import {fromModule} from 'util/styler/Styler'
import {getRelativeDate} from 'util/getRelativeDate'
import {history} from 'instantsearch.js/es/lib/routers/index.js'
import {useIDFromAnchorBlock} from 'util/useAnchor'
import {useLocalStorage} from 'util/useLocalStorage'
import {useTranslation} from 'util/i18'

const styles = fromModule(css)

const algoliaClient = algoliasearch(
	process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
	process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
)

type JobsHighlight = {
	value: string
	matchedWords: string[]
}

type JobsHit = {
	date: string
	expiration_date?: string
	node_id: number
	objectID: string
	title: string
	url: string
	short_description: string
	description: string
	tagline: string
	_highlightResult: {
		title: JobsHighlight
		tagline: JobsHighlight
		short_description: JobsHighlight
	}
}

export const Jobs: React.FC<DPJobsBlock> = (data) => {
	const id = useIDFromAnchorBlock(data)
	return (
		<Block type={data._type} anchorID={id}>
			<JobsView {...data} />
		</Block>
	)
}

const JobsView: React.FC<DPJobsBlock> = (params) => {
	const indexName = process.env.NEXT_PUBLIC_ALGOLIA_INDEX + '_jobs'
	const {types, csc_opportunities: csc} = params

	const [showBookmarked, setShowBookmarked] = useState<boolean>(false)
	const [bookmarkedHits, setBookmarkedHits] = useState<Hit<JobsHit>[]>([])
	const [bookmarkedIds, setBookmarkedIds] = useLocalStorage<number[]>(
		'bookmarked_jobs',
		[]
	)
	const toggleBookmark = (id: number) => {
		const newBookMarks = bookmarkedIds.slice()
		const idIndex = newBookMarks.indexOf(id)
		if (idIndex < 0) {
			newBookMarks.push(id)
		} else {
			newBookMarks.splice(idIndex, 1)
		}
		setBookmarkedIds(newBookMarks)
	}

	const getBookmarkedHits = () => {
		algoliaClient
			.initIndex(indexName)
			.search('', {
				hitsPerPage: bookmarkedIds.length,
				filters:
					'node_id:' +
					(bookmarkedIds.length
						? bookmarkedIds.join(' OR node_id:')
						: 'noBookmarks')
			})
			.then((res) => {
				if (res.hits.length && res.hits.length < bookmarkedIds.length) {
					setBookmarkedIds(res.hits.map((item: JobsHit) => item.node_id))
				}
				setBookmarkedHits(res.hits as Hit<JobsHit>[])
			})
			.catch(() => {
				console.error('Could not fetch bookmarked jobs')
			})
			.finally(() => {
				setShowBookmarked(true)
			})
	}

	const [today] = useState(new Date())
	const dateMenu: {label: string; start?: number}[] = useMemo(
		() => [
			{
				label: 'all'
			},
			{
				label: 'past_day',
				start: getUnixTime(startOfDay(sub(today, {days: 1})))
			},
			{
				label: 'past_3days',
				start: getUnixTime(startOfDay(sub(today, {days: 3})))
			},
			{
				label: 'past_week',
				start: getUnixTime(startOfDay(sub(today, {weeks: 1})))
			},
			{
				label: 'past_month',
				start: getUnixTime(startOfDay(sub(today, {months: 1})))
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const routing = useMemo(() => {
		return {
			router: history({
				writeDelay: 800
			}),
			stateMapping: {
				stateToRoute(uiState) {
					const {configure, query, page, numericMenu, refinementList} =
						uiState[indexName]
					const newRoute = {
						type: null,
						bookmarked: null,
						query: query,
						page: null,
						filters: []
					}
					if (configure?.filters?.indexOf('show_academic:true') >= 0)
						newRoute.type = 'academic'
					if (configure?.filters?.indexOf('show_career:true') >= 0)
						newRoute.type = 'career'
					if (page && page > 1) newRoute.page = page - 1
					if (numericMenu && numericMenu.date_timestamp) {
						const startTime = parseInt(numericMenu.date_timestamp.split(':')[0])
						const foundMenuItem = dateMenu.find(
							(di) => di.start && di.start === startTime
						)
						if (foundMenuItem) newRoute.filters.push(foundMenuItem.label)
					}
					if (refinementList && Object.keys(refinementList).length) {
						for (const refKey in refinementList) {
							newRoute.filters = newRoute.filters.concat(refinementList[refKey])
						}
					}

					let setEmpty = newRoute.type === 'career'
					for (const rKey in newRoute) {
						if (!setEmpty) break
						if (Array.isArray(newRoute[rKey])) {
							if (newRoute[rKey].length) setEmpty = false
							continue
						}
						if (newRoute[rKey] != null && rKey !== 'type') setEmpty = false
					}
					if (setEmpty) delete newRoute.type

					return Object.fromEntries(
						Object.entries(newRoute).filter(([_, v]) => v != null)
					)
				},
				routeToState(routeState) {
					const {type, query, page, filters} = routeState
					const newState = {
						configure: {
							filters: csc
								? `csc:true AND external:false`
								: `show_${type ? type : 'career'}:true`
						},
						query: query,
						page: null
					}

					if (page) newState.page = parseInt(page) + 1
					if (filters && filters.length) {
						for (const filter of filters) {
							// check is date filter
							if (
								['past_day', 'past_3days', 'past_week', 'past_month'].indexOf(
									filter
								) >= 0
							) {
								newState['numericMenu'] = {
									date_timestamp: `${dateMenu
										.find((di) => di.label === filter)
										.start.toString()}:`
								}
								continue
							}
							// map the rest to refinementList
							for (const typeKey in types) {
								const algoliaKey = algoliaFilterMap[typeKey]
								for (const typeItem of types[typeKey]) {
									if (typeItem.tid != filter) continue
									if (!newState['refinementList'])
										newState['refinementList'] = {}
									if (!newState['refinementList'][algoliaKey])
										newState['refinementList'][algoliaKey] = []
									newState['refinementList'][algoliaKey].push(filter)
								}
							}
						}
					}

					return {
						[indexName]: Object.fromEntries(
							Object.entries(newState).filter(([_, v]) => v != null)
						)
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const InstantSearchView =
		InstantSearch as unknown as React.FC<InstantSearchProps>
	return (
		<div className={styles.jobs()}>
			<InstantSearchView
				searchClient={algoliaClient}
				indexName={indexName}
				routing={routing}
			>
				<Configure />
				<JobsSettings
					{...params}
					dateMenu={dateMenu}
					setShowBookmarked={setShowBookmarked}
				/>
				<Theme.Container className={styles.jobs.alerts()}>
					<Suspense>
						<JobAlerts {...params} />
					</Suspense>
					<JobsBookmark
						bookmarkedIds={bookmarkedIds}
						showBookmarked={showBookmarked}
						setShowBookmarked={setShowBookmarked}
						getBookmarkedHits={getBookmarkedHits}
					/>
				</Theme.Container>
				<JobsResults
					bookmarkedIds={bookmarkedIds}
					showBookmarked={showBookmarked}
					bookmarkedHits={bookmarkedHits}
					toggleBookmark={toggleBookmark}
				/>
			</InstantSearchView>
		</div>
	)
}

const JobsSettings: React.FC<
	DPJobsBlock & {
		dateMenu: {label: string; start?: number}[]
		setShowBookmarked: (v: boolean) => void
	}
> = ({csc_opportunities: csc, types, dateMenu, setShowBookmarked}) => {
	const {indexUiState} = useInstantSearch()
	const getType = () => {
		const configureObj: any = indexUiState?.configure
		if (!configureObj || !configureObj.filters) return ''
		if (
			configureObj.filters.indexOf('show_academic:true') >= 0 ||
			configureObj.filters.indexOf('csc:true') >= 0
		)
			return 'academic'
		return 'career'
	}

	useEffect(() => {
		setShowBookmarked(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [indexUiState])

	return (
		<>
			<JobsTabs csc={csc} type={getType()} />
			<JobsSearch />
			<div className={styles.search.filters()}>
				<Theme.Container>
					<Jobsfilters
						types={types}
						tab={getType() === 'academic' ? 'academic' : 'career'}
						dateMenu={dateMenu}
					/>
				</Theme.Container>
			</div>
		</>
	)
}

const JobsTabs: React.FC<{
	csc: boolean
	type: string
}> = ({csc, type}) => {
	const {indexUiState} = useInstantSearch()
	const {refine} = useConfigure(indexUiState.configure)
	const {refine: resetPage} = usePagination()

	let tabs = []
	if (!csc) {
		tabs = [
			{key: 'career', icon: 'Career' as const, label: 'Career opportunities'},
			{
				key: 'academic',
				icon: 'Academic' as const,
				label: 'Academic opportunities'
			}
		]
	}

	return (
		<div className={styles.tabs()}>
			<Theme.Container>
				<div className={styles.tabs.tabs()}>
					<div className={styles.tabs.tabs.group.mod('items')()}>
						{tabs.map((tab) => (
							<Link
								key={tab.key}
								onClick={(e) => {
									e.preventDefault()
									resetPage(0)
									refine({filters: `show_${tab.key}:true`} as any)
								}}
								className={styles.tabs.item.is({
									active: type === tab.key || (!type && tab.key == 'career')
								})()}
							>
								<Icon
									className={styles.tabs.item.icon()}
									icon={tab.icon}
									nofill={true}
								/>
								{tab.label}
							</Link>
						))}
					</div>
				</div>
			</Theme.Container>
		</div>
	)
}

const JobsSearch: React.FC = () => {
	const autoCompleteIndex = process.env.NEXT_PUBLIC_ALGOLIA_INDEX + '_jobs_qs'
	const {readingroom} = useTranslation()

	return (
		<div className={styles.search()}>
			<div className={styles.search.query()}>
				<Theme.Container>
					<div className={styles.search.query.flex()}>
						<div className={styles.search.query.flex.input()}>
							<Autocomplete
								searchClient={algoliaClient}
								className={styles.autocomplete()}
								onSearch={() => {}}
								placeholder={readingroom.searchbar}
								detachedMediaQuery="none"
								openOnFocus
								autoFocus
								searchAsYouType={false}
								qsIndex={autoCompleteIndex}
							/>
						</div>
					</div>
				</Theme.Container>
			</div>
		</div>
	)
}

const JobsResults: React.FC<{
	bookmarkedIds: number[]
	toggleBookmark: (id: number) => void
	showBookmarked?: boolean
	bookmarkedHits?: Hit<JobsHit>[]
}> = ({bookmarkedIds, toggleBookmark, showBookmarked, bookmarkedHits = []}) => {
	const {hits, isLastPage, showMore, sendEvent, results} =
		useInfiniteHits<JobsHit>({
			showPrevious: false,
			escapeHTML: false
		})
	const t = useTranslation()
	return (
		<div className={styles.jobs.results()}>
			{(showBookmarked ? bookmarkedHits : hits).map((hit) => (
				<JobsItem
					job={hit}
					key={hit.url as string}
					bookmarkedIds={bookmarkedIds}
					toggleBookmark={toggleBookmark}
					query={results.query}
				/>
			))}
			{!showBookmarked && !isLastPage && showMore && (
				<SearchresultsMore onClick={showMore}>
					{`${t.jobs.loadmore} (${results.nbHits - hits.length})`}
				</SearchresultsMore>
			)}
		</div>
	)
}

const JobsItem: React.FC<{
	job: Hit<JobsHit>
	bookmarkedIds: number[]
	toggleBookmark: (id: number) => void
	query?: string
}> = ({job, bookmarkedIds, toggleBookmark, query}) => {
	const jobDate = new Date(Date.parse(job.date.replace(/-/g, '/')))

	const getDescription = useMemo(() => {
		if (
			job._highlightResult.title?.matchedWords?.length ||
			job._highlightResult.short_description?.matchedWords?.length ||
			!query
		)
			return job._highlightResult.short_description.value
		const foundIndex = job.description
			.toLowerCase()
			.indexOf(query.toLowerCase())
		if (foundIndex < 0) return job._highlightResult.short_description.value
		const ac = '[\\s\\S]'
		const regDesc = new RegExp(
			`${ac}*?(\\b${ac}{0,100}${query}${ac}{0,150}\\b)${ac}*`,
			'gim'
		)
		const regQry = new RegExp(`(${query})`, 'gim')
		return job.description
			.replace(regDesc, '...$1...')
			.replace(regQry, '<strong>$1</strong>')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div className={styles.item()}>
			<Theme.Container>
				<div className={styles.item.title()}>
					<Theme.H4>
						<Link href={job.url} className={styles.item.title.link()}>
							<Theme.Wysiwyg>{job._highlightResult.title.value}</Theme.Wysiwyg>
						</Link>
					</Theme.H4>
					<JobsBookmarkIcon
						active={bookmarkedIds.indexOf(job.node_id) >= 0}
						onClick={() => toggleBookmark(job.node_id)}
						title="Bookmark this job"
					/>
				</div>
				<div className={styles.item.subtitle()}>
					<Theme.Wysiwyg>
						{(job.tagline ? `${job._highlightResult.tagline.value} | ` : '') +
							getRelativeDate(jobDate)}
					</Theme.Wysiwyg>
				</div>
				{getDescription && (
					<div className={styles.item.description()}>
						<Theme.Wysiwyg>{getDescription}</Theme.Wysiwyg>
					</div>
				)}
			</Theme.Container>
		</div>
	)
}

const JobsBookmark: React.FC<{
	getBookmarkedHits: () => void
	showBookmarked: boolean
	setShowBookmarked: (v: boolean) => void
	bookmarkedIds: number[]
}> = ({
	getBookmarkedHits,
	showBookmarked,
	setShowBookmarked,
	bookmarkedIds
}) => {
	const t = useTranslation()

	useEffect(() => {
		if (!showBookmarked) return
		getBookmarkedHits()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookmarkedIds])

	return (
		<div>
			<Link
				onClick={(e) => {
					e.preventDefault()
					if (showBookmarked) {
						setShowBookmarked(false)
						return
					}
					getBookmarkedHits()
				}}
				className={styles.bookmark.is({
					selected: showBookmarked
				})()}
			>
				<JobsBookmarkIcon active={bookmarkedIds.length > 0} />
				{`${bookmarkedIds.length} ${
					bookmarkedIds.length === 1 ? t.jobs.job_saved : t.jobs.jobs_saved
				}`}
			</Link>
		</div>
	)
}

const JobsBookmarkIcon: React.FC<
	{
		active: boolean
	} & HTMLProps<HTMLDivElement>
> = ({active, ...rest}) => {
	return (
		<Icon
			{...rest}
			icon={'Bookmark'}
			nofill={true}
			className={styles.bookmarkicon.mergeProps(rest).is({active})()}
		/>
	)
}
